import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Self-hosted worker to avoid CDN dependency. The `?url` suffix makes Vite emit
// the worker as a hashed asset and hand back its URL — the supported pattern for
// pdfjs-dist v5 module workers under Vite — guaranteeing the worker version
// matches the pdfjs API version react-pdf uses.
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

interface PdfViewerProps {
  url: string;
  /** Override the CSS width in px. Defaults to 100% of parent container. */
  width?: number;
}

/**
 * Renders a PDF from a URL via pdf.js. Used both for sitter-facing receipt
 * previews and the public /view/:nonce page so behaviour is consistent across
 * iOS Safari, Android Chrome, and desktop — inline object/embed PDFs are
 * unreliable on mobile.
 */
export function PdfViewer({ url, width }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(width);

  // Size the page to the actual parent container, not the window — otherwise an
  // 800px page overflows the surrounding card and the right edge gets clipped by
  // the overflow-hidden wrapper. Leave a small horizontal inset (split evenly by
  // the centering wrapper below) so the page sits inside the border on both sides.
  useEffect(() => {
    if (width) return;
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(Math.max(0, el.clientWidth - 48));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [width]);

  if (error) {
    return (
      <div className="border border-slate-200 rounded p-6 text-center space-y-2">
        <p className="text-sm text-slate-600">Couldn't render PDF inline.</p>
        {/* Surface the underlying pdf.js error — "worker failed" vs "fetch
            blocked" vs "invalid PDF" need entirely different fixes, and
            hiding it here made the July 2026 reports undiagnosable. */}
        <p className="text-xs text-slate-400 break-words">{error}</p>
        <a href={url} className="text-sky-600 hover:text-sky-700 text-sm" target="_blank" rel="noreferrer">
          Open PDF in a new tab
        </a>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="border border-slate-200 rounded overflow-hidden flex flex-col items-center">
      <Document
        file={url}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        onLoadError={(e) => {
          console.error('PDF inline render failed:', e);
          setError(e.message);
        }}
        loading={<div className="p-6 text-sm text-slate-500">Loading PDF…</div>}
      >
        {numPages &&
          Array.from({ length: numPages }, (_, i) => (
            <Page
              key={i}
              pageNumber={i + 1}
              width={containerWidth}
              renderAnnotationLayer
              renderTextLayer
            />
          ))}
      </Document>
    </div>
  );
}
